import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ShoppingCart, User, Store, ChefHat, Truck, Menu, X, Building2, Package, ClipboardList, Users, Settings, Tag, Plus, DollarSign, Wallet, TrendingUp, Scan } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import { useStore } from '../contexts/StoreContext'
import { useState } from 'react'
import ProfilePhoto from './ProfilePhoto'

const Navbar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, isStoreOwner, isDispatcher, isChef, isDelivery, isPlatformAdmin, viewMode, isEmployee } = useAuth()
  const { itemCount } = useCart()
  
  // Usar useStore - ahora retorna valores por defecto si no está disponible
  const storeContext = useStore()
  const activeStore = storeContext?.activeStore || null
  const hasMultipleStores = storeContext?.hasMultipleStores || false
  
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const getNavItems = () => {
    if (!user) {
      return [
        { path: '/', label: 'Inicio', icon: Store },
        { path: '/stores', label: 'Negocios', icon: Store },
      ]
    }

    // Si el usuario es empleado pero está en modo cliente, mostrar navegación de cliente
    if (isEmployee && viewMode === 'customer') {
      return [
        { path: '/', label: 'Inicio', icon: Store },
        { path: '/stores', label: 'Negocios', icon: Store },
        { path: '/cart', label: 'Carrito', icon: ShoppingCart, ...(itemCount > 0 && { badge: itemCount }) },
        { path: '/orders', label: 'Pedidos', icon: User },
      ]
    }

    if (isStoreOwner) {
      const navItems = [
        { path: '/store-owner/my-stores', label: 'Mis Negocios', icon: Building2 },
        { path: '/create-store', label: 'Crear Negocio', icon: Plus },
      ]

      // Agregar dashboard de rendimiento si tiene múltiples negocios
      if (hasMultipleStores) {
        navItems.push(
          { path: '/store/dashboard', label: 'Rendimiento', icon: TrendingUp }
        )
      }

      // Agregar navegación específica del negocio si hay uno activo
      if (activeStore) {
        navItems.push(
          { path: '/store-owner/dashboard', label: 'Panel', icon: Building2 },
          { path: `/store/${activeStore.id}/products`, label: 'Inventario', icon: Package },
          { path: `/store/${activeStore.id}/inventory`, label: 'Escáner', icon: Scan },
          { path: '/store/categories', label: 'Categorías', icon: Tag },
          { path: `/store/${activeStore.id}/orders`, label: 'Órdenes', icon: ClipboardList },
          { path: `/store/${activeStore.id}/cash-register`, label: 'Caja', icon: DollarSign },
          { path: `/store/${activeStore.id}/delivery-wallets`, label: 'Carteras Delivery', icon: Wallet },
          { path: `/store/${activeStore.id}/employees`, label: 'Empleados', icon: Users },
          { path: `/store/${activeStore.id}/settings`, label: 'Configuración', icon: Settings }
        )
      }

      return navItems
    }

    if (isDispatcher) {
      return [
        { path: '/dispatcher', label: 'Despacho', icon: Building2 },
        { path: `/store/${user.storeId}/orders`, label: 'Órdenes', icon: ClipboardList },
        { path: `/store/${user.storeId}/products`, label: 'Inventario', icon: Package },
        { path: `/store/${user.storeId}/inventory`, label: 'Escáner', icon: Scan },
        { path: `/store/${user.storeId}/cash-register`, label: 'Caja', icon: DollarSign },
        { path: `/store/${user.storeId}/delivery-wallets`, label: 'Carteras Delivery', icon: Wallet },
      ]
    }

    if (isChef) {
      return [
        { path: `/store/${user.storeId}/kitchen`, label: 'Cocina', icon: ChefHat },
      ]
    }

    if (isDelivery) {
      return [
        { path: user.storeId ? `/store/${user.storeId}/delivery` : '/store/delivery', label: 'Entregas', icon: Truck },
        { path: '/store/delivery/wallet', label: 'Cartera', icon: Wallet },
        { path: '/profile', label: 'Perfil', icon: User },
      ]
    }

    if (isPlatformAdmin) {
      return [
        { path: '/admin/dashboard', label: 'Panel', icon: Building2 },
        { path: '/admin/orders', label: 'Pedidos', icon: Package },
        { path: '/admin/users', label: 'Usuarios', icon: User },
        { path: '/admin/categories', label: 'Categorías', icon: Tag },
        { path: '/admin/store-owners', label: 'Dueños', icon: Store },
        { path: '/admin/stores', label: 'Aprobaciones', icon: Store },
        { path: '/admin/store-owner-requests', label: 'Solicitudes', icon: User },
      ]
    }

    // Customer
    return [
      { path: '/', label: 'Inicio', icon: Store },
      { path: '/stores', label: 'Colmados', icon: Store },
      { path: '/cart', label: 'Carrito', icon: ShoppingCart, ...(itemCount > 0 && { badge: itemCount }) },
      { path: '/orders', label: 'Pedidos', icon: User },
    ]
  }

  const navItems = getNavItems()

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'store_owner': return 'Dueño de Negocio'
      case 'store_admin': return 'Administrador de Negocio'
      case 'customer': return 'Cliente'
      case 'dispatcher': return 'Despachador'
      case 'chef': return 'Cocinero'
      case 'delivery': return 'Repartidor'
      case 'platform_admin': return 'Administrador de Plataforma'
      default: return type.replace('_', ' ')
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
    setIsMenuOpen(false)
  }

  return (
    <nav className="bg-white/80 backdrop-blur-2xl shadow-sm border-b border-gray-200/60 fixed top-0 left-0 right-0 z-[9999]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img
              src="/image/ColmaFacil-Logo.png"
              alt="ColmaFácil"
              className="h-7 sm:h-8 w-auto transition-transform duration-200 group-hover:scale-105"
              onError={(e) => {
                // Fallback si la imagen no se encuentra
                const target = e.target as HTMLImageElement
                target.src = '/image/ColmaFacil-Icono.png'
              }}
            />
          </Link>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.slice(0, 4).map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              const isProfile = item.path === '/profile'
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'text-primary-600 bg-primary-50 shadow-sm border border-primary-200/50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50/80'
                  }`}
                >
                  {isProfile && user ? (
                    <ProfilePhoto 
                      photoUrl={user.profilePhotoUrl || (user as any).profile_photo_url} 
                      size="xs" 
                      showBorder={false}
                    />
                  ) : (
                  <Icon className="w-4 h-4" />
                  )}
                  <span>{item.label}</span>
                  {'badge' in item && item.badge && Number(item.badge) > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold shadow-md px-1.5">
                      {Number(item.badge) > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            {user ? (
              <Link
                to="/profile"
                className="flex items-center space-x-2 sm:space-x-3 p-1.5 sm:p-2 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-gray-50/80 transition-all duration-200 group"
                title="Ir al perfil"
              >
                <ProfilePhoto photoUrl={user.profilePhotoUrl || (user as any).profile_photo_url} size="md" showBorder={false} className="group-hover:scale-110 transition-transform duration-200" />
                <div className="hidden md:block">
                  <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                  <div className="text-xs text-gray-500">{getUserTypeLabel(user.type)}</div>
                </div>
              </Link>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login" className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-200">
                  Iniciar Sesión
                </Link>
                <Link to="/register" className="px-3 sm:px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-all duration-200 shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30 active:scale-95">
                  Registrarse
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 transition-all duration-200 active:scale-95"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-200/60 bg-white/95 backdrop-blur-xl">
          <div className="px-4 pt-3 pb-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              const isProfile = item.path === '/profile'
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${
                    isActive
                      ? 'text-primary-600 bg-primary-50 border border-primary-200/50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50/80'
                  }`}
                >
                  {isProfile && user ? (
                    <ProfilePhoto 
                      photoUrl={user.profilePhotoUrl || (user as any).profile_photo_url} 
                      size="sm" 
                      showBorder={false}
                    />
                  ) : (
                  <Icon className="w-5 h-5" />
                  )}
                  <span>{item.label}</span>
                  {'badge' in item && item.badge && Number(item.badge) > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full min-w-[22px] h-5 flex items-center justify-center font-bold shadow-sm px-1.5">
                      {Number(item.badge) > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              )
            })}

            {user && (
              <>
                <div className="border-t border-gray-200 my-3"></div>
                <Link
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl text-base font-semibold text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200"
                >
                  {(user?.profilePhotoUrl || (user as any)?.profile_photo_url) ? (
                    <ProfilePhoto photoUrl={user.profilePhotoUrl || (user as any).profile_photo_url} size="sm" showBorder={false} className="transition-transform duration-200" />
                  ) : (
                  <User className="w-5 h-5" />
                  )}
                  <span>Mi Perfil</span>
                </Link>
                <div className="px-4 py-3 bg-gray-50 rounded-xl">
                  <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{getUserTypeLabel(user.type)}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl text-base font-semibold text-gray-700 hover:text-red-600 hover:bg-red-50 w-full text-left transition-all duration-200"
                >
                  <X className="w-5 h-5" />
                  <span>Cerrar Sesión</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
